import { useSelector } from 'react-redux';
import useMonitoringQueueGroups from 'hooks/useMonitoringQueueGroups';
import useQueues from 'hooks/useQueues';
import useVoiceChannels from 'hooks/useVoiceChannels';
import { useVerifyRolesContext } from 'hooks/useVerifyRoles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Card, CardBody, CardFooter, Col, Container, Row } from 'reactstrap';
import { URLHelper } from 'utils/classes/URLHelper';
import ListWatingCalls from './components/ListWatingCalls';
import NothingToShow from './components/NothingToShow';
import QueueMembersContainer from './components/QueueMembers/QueueMembersContainer';
import useAgentEvents from './hooks/useAgentEvents';
import { useBFFQueueMonitoring } from './hooks/useBFFQueueMonitoring';
import { useSocketWatingCalls } from './hooks/useSocketWatingCalls';
import { TIME_INTERVAL } from './utils/timeIntervalOfRequests';
import { useGeneralQueueConfig } from 'hooks/useGeneralQueueConfig';
import { useServiceLevels } from 'hooks/useServiceLevels';
import { useDragAndReorder } from 'hooks/useDragAndReorder';

import { MonitoringHeader } from './components/MonitoringHeader';
import { StatsSidebar } from './components/StatsSidebar';
import { TelephonyAnalyticsDashboard } from './components/TelephonyAnalyticsDashboard';
import {
  getCardStatsConfig,
  getCircleStatsConfig,
  getAgentsStatusCounts,
  getFilteredQueueMembers
} from './logic';

import {
  loadObjectFromLocalStorage,
  saveObjectToLocalStorage,
  LOCAL_STORAGE_KEYS
} from 'utils/fuctions/localStorage';

/**
 * Hook personalizado para persistir estado no localStorage.
 */
function useStickyState(defaultValue, key) {
  const [value, setValue] = useState(() => {
    try {
      const stickyValue = loadObjectFromLocalStorage(key);
      return stickyValue !== null && stickyValue !== undefined
        ? stickyValue
        : defaultValue;
    } catch (error) {
      console.error(`Erro ao ler ${key} do localStorage:`, error);
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      saveObjectToLocalStorage(key, value);
    } catch (error) {
      console.error(`Erro ao salvar ${key} no localStorage:`, error);
    }
  }, [key, value]);

  return [value, setValue];
}

const MonitoringPanel = () => {
  const history = useHistory();
  const { roles } = useVerifyRolesContext();
  const systemConfiguration = useSelector((state) => state.systemConfiguration);

  const { response: allQueuesList } = useQueues({
    types: ['is_telephony'],
    showDisabled: false,
    shouldUseOnlyAllowedUserQueues: true
  });

  const { options: allVoiceChannels } = useVoiceChannels();

  const { response: savedGroups, fetch: fetchGroups } =
    useMonitoringQueueGroups({
      itemsPerPage: 100
    });

  const { groupedByType: serviceLevelsByType } = useServiceLevels({
    targets: ['TELEPHONY']
  });
  const telephonyServiceLevelConfig = serviceLevelsByType?.TELEPHONY?.[0];

  const { config: generalConfig } = useGeneralQueueConfig();

  const [selectedGroup, setSelectedGroup] = useStickyState(
    null,
    LOCAL_STORAGE_KEYS.MONITORING_PANEL_SELECTED_GROUP
  );

  const [manualSelection, setManualSelection] = useStickyState(
    null,
    LOCAL_STORAGE_KEYS.MONITORING_PANEL_MANUAL_SELECTION
  );

  const [selectedVoiceChannels, setSelectedVoiceChannels] = useStickyState(
    null,
    LOCAL_STORAGE_KEYS.MONITORING_PANEL_VOICE_CHANNELS
  );

  const [cardOrder, setCardOrder] = useState([
    'TMA',
    'TMO',
    'TME',
    'LONGEST_WAIT',
    'ANSWERED_PERCENTAGE',
    'SERVICE_LEVEL',
    'WAITING_QUEUE',
    'ATTENDANCE_LEVEL_QTY',
    'SERVICE_LEVEL_PERCENTAGE'
  ]);

  const [activeFilters, setActiveFilters] = useState([]);

  const [matrixSorting, setMatrixSorting] = useState([]);
  const [matrixColumnOrder, setMatrixColumnOrder] = useState([]);
  const [matrixColumnVisibility, setMatrixColumnVisibility] = useState({});

  const [sortType, setSortType] = useStickyState(
    'extension',
    LOCAL_STORAGE_KEYS.MONITORING_PANEL_SORT_TYPE
  );
  const [isOneCardPerAgent, setIsOneCardPerAgent] = useStickyState(
    true,
    LOCAL_STORAGE_KEYS.MONITORING_PANEL_IS_ONE_CARD_PER_AGENT
  );
  const [filterValue, setFilterValue] = useStickyState(
    '',
    LOCAL_STORAGE_KEYS.MONITORING_PANEL_FILTER_VALUE
  );
  const [showAgentStatistics, setShowAgentStatistics] = useStickyState(
    false,
    LOCAL_STORAGE_KEYS.MONITORING_PANEL_SHOW_AGENT_STATISTICS
  );
  const [groupByQueue, setGroupByQueue] = useStickyState(
    false,
    LOCAL_STORAGE_KEYS.MONITORING_PANEL_GROUP_BY_QUEUE
  );
  const [layoutOption, setLayoutOption] = useState('one_card_per_agent');
  const [search, setSearch] = useState({ isActive: false, value: '' });

  const [agentsOffline, setagentsOffline] = useState([]);

  useEffect(() => {
    if (manualSelection && !Array.isArray(manualSelection)) {
      setManualSelection([]);
    }
  }, [manualSelection, setManualSelection]);

  useEffect(() => {
    if (selectedVoiceChannels && !Array.isArray(selectedVoiceChannels)) {
      setSelectedVoiceChannels([]);
    }
  }, [selectedVoiceChannels, setSelectedVoiceChannels]);

  useEffect(() => {
    if (
      allVoiceChannels &&
      allVoiceChannels.length > 0 &&
      selectedVoiceChannels === null
    ) {
      setSelectedVoiceChannels(allVoiceChannels.map((c) => String(c.value)));
    }
  }, [allVoiceChannels, selectedVoiceChannels, setSelectedVoiceChannels]);

  useEffect(() => {
    if (
      !selectedGroup &&
      manualSelection === null &&
      allQueuesList.length > 0
    ) {
      setManualSelection(allQueuesList);
    }
  }, [selectedGroup, manualSelection, allQueuesList, setManualSelection]);

  const handleRefreshGroups = useCallback(() => {
    fetchGroups(1, '', { ignoreCache: true });
  }, [fetchGroups]);

  useEffect(() => {
    handleRefreshGroups();
  }, [handleRefreshGroups]);

  useEffect(() => {
    if (selectedGroup && savedGroups.length > 0) {
      const updatedGroup = savedGroups.find(
        (g) => String(g.id) === String(selectedGroup.id)
      );

      if (updatedGroup) {
        const getQueueSignature = (g) =>
          (g.queues || [])
            .map((q) => q.id || q)
            .sort((a, b) => a - b)
            .join(',');

        const getAgentSignature = (g) =>
          (g.agents || [])
            .map((a) => a.login)
            .sort()
            .join(',');

        const currentSig = getQueueSignature(selectedGroup);
        const newSig = getQueueSignature(updatedGroup);

        const currentAgentSig = getAgentSignature(selectedGroup);
        const newAgentSig = getAgentSignature(updatedGroup);

        if (
          currentSig !== newSig ||
          selectedGroup.name !== updatedGroup.name ||
          currentAgentSig !== newAgentSig
        ) {
          setSelectedGroup(updatedGroup);
        }
      } else {
        setSelectedGroup(null);
      }
    }
  }, [savedGroups, selectedGroup, setSelectedGroup]);

  useEffect(() => {
    const reloadTimeMinutes = Number(generalConfig?.monitoringReloadTime);
    if (!reloadTimeMinutes || reloadTimeMinutes <= 0) return;
    const timer = setTimeout(
      () => window.location.reload(),
      reloadTimeMinutes * 60 * 1000
    );
    return () => clearTimeout(timer);
  }, [generalConfig?.monitoringReloadTime]);

  const legacyUrlData = useMemo(() => {
    const search = window.location.search;
    if (!search) return { ids: [], names: [] };

    try {
      const ids = URLHelper.getParamAsArray('ids');
      const names = URLHelper.getParamAsArray('names');

      if (!ids.length) {
        const params = new URLSearchParams(search);

        if (params.has('queuesId')) {
          return {
            ids: params.get('queuesId').split(','),
            names: params.get('queueNames')
              ? params.get('queueNames').split(',')
              : []
          };
        }

        return {
          ids: params.get('ids') ? params.get('ids').split(',') : [],
          names: params.get('names') ? params.get('names').split(',') : []
        };
      }
      return { ids, names };
    } catch (e) {
      return { ids: [], names: [] };
    }
  }, []);

  useEffect(() => {
    if (
      allQueuesList.length > 0 &&
      legacyUrlData.ids.length > 0 &&
      (!manualSelection || manualSelection === null) &&
      !selectedGroup
    ) {
      const initialQueues = allQueuesList.filter((q) =>
        legacyUrlData.ids.includes(String(q.id))
      );

      if (initialQueues.length > 0) {
        setManualSelection(initialQueues);
      }
    }
  }, [allQueuesList, legacyUrlData.ids]);

  const queuesToMonit = useMemo(() => {
    if (selectedGroup && selectedGroup.queues) {
      return selectedGroup.queues.map((q) => String(q.id || q));
    }
    if (Array.isArray(manualSelection) && manualSelection.length > 0) {
      return manualSelection.map((q) => String(q.id));
    }
    return legacyUrlData.ids;
  }, [selectedGroup, manualSelection, legacyUrlData.ids]);

  const activeAgentLogins = useMemo(() => {
    if (
      selectedGroup &&
      selectedGroup.agents &&
      selectedGroup.agents.length > 0
    )
      return selectedGroup.agents.map((a) => ({
        login: a.login,
        name: a.name
      }));
    return [];
  }, [selectedGroup]);

  const nameQueuesToMonit = useMemo(() => {
    if (selectedGroup && selectedGroup.queues) {
      return selectedGroup.queues.map((q) => q.name || `Fila ${q.id || q}`);
    }
    if (Array.isArray(manualSelection) && manualSelection.length > 0) {
      return manualSelection.map((q) => q.name);
    }
    return legacyUrlData.names;
  }, [selectedGroup, manualSelection, legacyUrlData.names]);

  const hasJustOneQueue = useMemo(
    () => queuesToMonit.length === 1 && !!queuesToMonit[0],
    [queuesToMonit]
  );

  const { agents, socket } = useAgentEvents(
    queuesToMonit,
    selectedVoiceChannels
  );

  const { watingCalls } = useSocketWatingCalls(socket, nameQueuesToMonit);

  const {
    queueMonitoringData,
    getQueueMonitoringData,
    getQueueMonitoringDataByQueue,
    queueMonitoringDataByQueue
  } = useBFFQueueMonitoring(
    queuesToMonit,
    selectedVoiceChannels,
    selectedGroup?.agents || []
  );

  useEffect(() => {
    const hasQueues =
      queuesToMonit && queuesToMonit.length > 0 && queuesToMonit[0] !== '';
    const hasChannels =
      selectedVoiceChannels && selectedVoiceChannels.length > 0;

    if (!hasQueues && !hasChannels) return;

    getQueueMonitoringData();
    if (groupByQueue) {
      getQueueMonitoringDataByQueue();
    }
    const interval = setInterval(() => {
      getQueueMonitoringData();
      if (groupByQueue) {
        getQueueMonitoringDataByQueue();
      }
    }, TIME_INTERVAL);

    return () => clearInterval(interval);
  }, [
    queuesToMonit,
    selectedVoiceChannels,
    getQueueMonitoringData,
    getQueueMonitoringDataByQueue,
    groupByQueue
  ]);

  useEffect(() => {
    const isOneCard = !hasJustOneQueue;
    setIsOneCardPerAgent(isOneCard);
    setLayoutOption(isOneCard ? 'one_card_per_agent' : 'one_card_per_queue');
  }, [hasJustOneQueue, setIsOneCardPerAgent, setLayoutOption]);

  const handleFilter = (e) => {
    setFilterValue(e.toLowerCase());
  };

  const sorters = useMemo(
    () => ({
      queue: (items) => {
        return [...items].sort((a, b) => {
          return a.queueName < b.queueName ? -1 : 1;
        });
      },
      extension: (items) => {
        return [...items].sort((a, b) => {
          return a.extension < b.extension ? -1 : 1;
        });
      },
      agent: (items) => {
        return [...items].sort((a, b) => {
          return a.agent?.usr?.name?.toLowerCase() <
            b.agent?.usr?.name?.toLowerCase()
            ? -1
            : 1;
        });
      }
    }),
    []
  );

  const groupByAgent = useCallback((allCardsData) => {
    if (!allCardsData) return [];

    const groupedAgents = {};
    allCardsData.forEach((item) => {
      if (!groupedAgents[item?.agent?.usr?.login]) {
        groupedAgents[item?.agent?.usr?.login] = item;
        groupedAgents[item?.agent?.usr?.login].sessions = [];
      }

      groupedAgents[item?.agent?.usr?.login].sessions.push(item?.agent?.queue);
    });

    const cardDataByAgentDTO = [];
    for (const key in groupedAgents) {
      cardDataByAgentDTO.push(groupedAgents[key]);
    }

    return cardDataByAgentDTO;
  }, []);

  const QueueMembers = useMemo(() => {
    const currentSorter = sorters[sortType] || sorters.extension;

    if (isOneCardPerAgent) {
      const groupedCardData = groupByAgent(agents || []);
      return currentSorter(groupedCardData);
    }
    return currentSorter(agents);
  }, [groupByAgent, isOneCardPerAgent, agents, sorters, sortType]);

  const queueMemberInfo = useMemo(() => {
    const hasAgentFilter = activeAgentLogins && activeAgentLogins.length > 0;
    return QueueMembers.filter((item) => {
      const offlineAgent = QueueMembers?.find((agent) => agent?.userOffline);
      const userOfflineList =
        QueueMembers?.find((item) => Array.isArray(item?.userOffline))
          ?.userOffline || [];

      const agentNames = QueueMembers.filter(
        (item) => item?.agent && item?.agent.usr && item?.agent?.usr?.name
      ).map((item) => item?.agent?.usr?.name);

      const filteredOfflineUsers = userOfflineList?.filter(
        (user) => !agentNames?.includes(user?.name)
      );
      if (activeAgentLogins && activeAgentLogins.length > 0) {
        const filteredOfflineUsersWithActiveLogins =
          filteredOfflineUsers.filter((user) =>
            activeAgentLogins.some((agent) => agent.name === user.name)
          );
        setagentsOffline(filteredOfflineUsersWithActiveLogins);
      } else if (offlineAgent && offlineAgent?.userOffline) {
        setagentsOffline(filteredOfflineUsers);
      }

      if (hasAgentFilter) {
        const agentLogin = item?.agent?.usr?.login;
        if (
          !agentLogin ||
          !activeAgentLogins.some((agent) => agent.login === agentLogin)
        ) {
          return false;
        }
      }

      if (isOneCardPerAgent) {
        return item.sessions.some((session) => {
          const isInQueue = queuesToMonit.includes(String(session?.id));
          const isInChannel = selectedVoiceChannels.includes(
            String(session?.voice_channel_id || session?.id)
          );
          return isInQueue || isInChannel;
        });
      }

      const isInQueue = queuesToMonit?.includes(String(item?.agent?.queue_id));
      const isInChannel = (selectedVoiceChannels || []).includes(
        String(item?.agent?.voice_channel_id)
      );

      return isInQueue || isInChannel;
    });
  }, [
    isOneCardPerAgent,
    queuesToMonit,
    selectedVoiceChannels,
    QueueMembers,
    activeAgentLogins
  ]);

  const agentsStatusCounts = useMemo(() => {
    return getAgentsStatusCounts(
      queueMemberInfo,
      watingCalls?.length || 0,
      agentsOffline?.length || 0
    );
  }, [queueMemberInfo, watingCalls, agentsOffline]);

  const circleStats = useMemo(
    () => getCircleStatsConfig(agentsStatusCounts, activeFilters),
    [agentsStatusCounts, activeFilters]
  );

  const cardStats = useMemo(() => {
    return getCardStatsConfig(
      queueMonitoringData,
      cardOrder,
      telephonyServiceLevelConfig
    );
  }, [queueMonitoringData, cardOrder, telephonyServiceLevelConfig]);

  const handleCircleClick = (prop) => {
    if (!prop.clickable) return;
    const key = prop.filterKey;

    setActiveFilters((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      return [...prev, key];
    });
  };

  const filteredQueueMembersContainer = useMemo(() => {
    return getFilteredQueueMembers(queueMemberInfo, activeFilters);
  }, [queueMemberInfo, activeFilters]);

  const {
    handleDragStart,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDragEnd
  } = useDragAndReorder(cardOrder, setCardOrder);

  const toggleSearch = useCallback(() => {
    setSearch((prev) => ({ ...prev, isActive: !prev?.isActive, value: '' }));
  }, []);

  const handleSearchChange = useCallback(
    (e) => {
      setSearch((prev) => ({ ...prev, value: e.target.value }));
      setFilterValue(e.target.value.toLowerCase());
    },
    [setFilterValue]
  );

  useEffect(() => {
    if (filterValue)
      setSearch((prev) => ({ ...prev, isActive: true, value: filterValue }));
  }, [filterValue]);

  useEffect(() => {
    const login = window.localStorage.getItem('login');
    const token = window.localStorage.getItem('token');
    if (!login || !token) return history.push('/admin');

    const [role] = (roles ?? []).filter(
      ({ role }) => role === 'MONITORING_CALLCENTER'
    );
    if (!role && roles != null) return history.push('/admin');
  }, [history, roles]);

  return (
    <div style={{ position: 'relative', overflowX: 'hidden' }}>
      <Container fluid style={{ marginTop: '-1rem' }}>
        <Row>
          <div className="col">
            <Card style={{ padding: '0', backgroundColor: '#eee' }}>
              <CardBody>
                <Row>
                  {!groupByQueue && !showAgentStatistics && (
                    <StatsSidebar
                      cardStats={cardStats}
                      systemConfiguration={systemConfiguration}
                      onDragStart={handleDragStart}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDragEnd={handleDragEnd}
                    />
                  )}

                  <Col>
                    <MonitoringHeader
                      systemConfiguration={systemConfiguration}
                      circleStats={circleStats}
                      onCircleClick={handleCircleClick}
                      activeFilters={activeFilters}
                      groups={savedGroups}
                      selectedGroup={selectedGroup}
                      onSelectGroup={(g) => {
                        setSelectedGroup(g);
                        setManualSelection([]);
                      }}
                      onClearGroup={() => {
                        setSelectedGroup(null);
                        setManualSelection([]);
                      }}
                      manualSelection={manualSelection}
                      onManualChange={(q) => {
                        setManualSelection(q);
                        setSelectedGroup(null);
                      }}
                      allQueues={allQueuesList}
                      onGroupsChanged={handleRefreshGroups}
                      searchState={search}
                      onToggleSearch={toggleSearch}
                      onSearchChange={handleSearchChange}
                      setSortOption={(val) => setSortType(val)}
                      setLayoutOption={(val) => {
                        setIsOneCardPerAgent(val === 'one_card_per_agent');
                        setGroupByQueue(val === 'one_card_per_queue');
                        setLayoutOption(val);
                      }}
                      layoutOption={layoutOption}
                      allVoiceChannels={allVoiceChannels}
                      selectedVoiceChannels={selectedVoiceChannels}
                      setSelectedVoiceChannels={setSelectedVoiceChannels}
                    />

                    {(!queuesToMonit.join() || queuesToMonit[0] === '') &&
                    (selectedVoiceChannels || []).length === 0 ? (
                      <NothingToShow />
                    ) : (
                      agents && (
                        <QueueMembersContainer
                          queueMonitoringDataByQueue={
                            queueMonitoringDataByQueue
                          }
                          watingCalls={watingCalls}
                          filterValue={filterValue}
                          allQueueMembers={filteredQueueMembersContainer}
                          isOneCardPerAgent={isOneCardPerAgent}
                          showAgentStatistics={showAgentStatistics}
                          nameQueuesToMonit={nameQueuesToMonit}
                          groupByQueue={groupByQueue}
                          usersOffline={agentsOffline}
                        />
                      )
                    )}
                  </Col>
                </Row>
                <Row>
                  <TelephonyAnalyticsDashboard
                    queueMonitoringData={queueMonitoringData}
                    tableState={{
                      sorting: matrixSorting,
                      setSorting: setMatrixSorting,
                      columnOrder: matrixColumnOrder,
                      setColumnOrder: setMatrixColumnOrder,
                      columnVisibility: matrixColumnVisibility,
                      setColumnVisibility: setMatrixColumnVisibility
                    }}
                  />
                </Row>
              </CardBody>
            </Card>
          </div>
        </Row>
      </Container>
      <ListWatingCalls watingCalls={watingCalls} />
    </div>
  );
};

export default MonitoringPanel;
